import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_BRAND_COLORS,
  JR_BRAND_SHAPES,
} from "@/utils/jrDemoState";
import { playClick, playCorrect } from "@/utils/jrSound";
import { speak } from "@/utils/jrVoice";
import { Palette, Sparkles } from "lucide-react";

export default function Mission5() {
  const navigate = useNavigate();
  const initial = loadJrState();
  const [name, setName] = useState(
    initial.brandName || initial.businessIdea || ""
  );
  const [colorKey, setColorKey] = useState(initial.brandColor || "sunny");
  const [shapeKey, setShapeKey] = useState(initial.brandShape || "lemon");
  const [showOverlay, setShowOverlay] = useState(false);

  const color = JR_BRAND_COLORS.find((c) => c.key === colorKey) || JR_BRAND_COLORS[0];
  const shape = JR_BRAND_SHAPES.find((s) => s.key === shapeKey) || JR_BRAND_SHAPES[0];

  const canContinue = name.trim().length >= 2;

  const onContinue = () => {
    if (!canContinue) return;
    saveJrState({
      brandName: name.trim(),
      brandColor: colorKey,
      brandShape: shapeKey,
    });
    // Customization: chose their own business name (not blank, not the demo default)
    markMissionComplete(5, { customized: true });
    playCorrect();
    speak(`Your brand is ready! ${name.trim()}. Looking sharp, Junior CEO!`);
    setShowOverlay(true);
  };

  const pickColor = (key) => {
    setColorKey(key);
    playClick();
  };
  const pickShape = (key) => {
    setShapeKey(key);
    playClick();
  };

  return (
    <ElementaryMissionPage
      missionNumber={5}
      title="Brand Your Business"
      lesson="A brand is how people recognize your idea. Pick a name, color, and shape that shows who you are."
      canContinue={canContinue}
      onContinue={onContinue}
      continueLabel="Save My Brand"
      testId="elementary-mission-5-page"
    >
      <div className="mb-5">
        <LemonBotJr
          greeting="Let's design your brand! A great brand has a name, a color, and a symbol kids will remember."
          prompts={[
            "Pick a short, catchy name (2-4 words).",
            "Pick a color that feels like your business.",
            "Pick a shape or symbol that matches your idea.",
          ]}
          compact
        />
      </div>

      {/* Live logo preview */}
      <div
        className="mb-6 bg-white ring-2 ring-yellow-300 rounded-3xl p-6 shadow-lg flex flex-col items-center gap-4"
        data-testid="mission-5-preview"
      >
        <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide inline-flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5" /> Live Logo Preview
        </p>
        <div
          className={`${color.bg} w-32 h-32 sm:w-40 sm:h-40 rounded-[2rem] shadow-lg flex items-center justify-center transition-all duration-300`}
          style={{ boxShadow: `0 10px 30px -10px ${color.hex}aa` }}
          aria-hidden="true"
        >
          <span className="text-7xl sm:text-8xl drop-shadow">{shape.emoji}</span>
        </div>
        <p className={`${color.text} text-2xl sm:text-3xl font-black text-center px-4 break-words`} data-testid="mission-5-preview-name">
          {name.trim() || "Your Brand Name"}
        </p>
      </div>

      {/* Name input */}
      <div className="mb-6">
        <label className="block text-base font-bold text-gray-900 mb-2">
          What's your business called?
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          placeholder="Pencil Power Station"
          className="w-full px-4 py-3 text-lg border-2 border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
          data-testid="mission-5-name-input"
        />
        <p className="mt-1 text-xs text-gray-500">{name.length}/40</p>
      </div>

      {/* Color picker */}
      <div className="mb-6">
        <p className="text-base font-bold text-gray-900 mb-3 inline-flex items-center gap-2">
          <Palette className="w-4 h-4 text-yellow-600" /> Pick a color
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {JR_BRAND_COLORS.map((c) => {
            const selected = colorKey === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => pickColor(c.key)}
                className={`${c.bg} rounded-2xl aspect-square ring-4 transition-all flex items-center justify-center ${
                  selected
                    ? `${c.ring} scale-105 shadow-lg`
                    : "ring-white hover:scale-105 hover:shadow-md"
                }`}
                data-testid={`mission-5-color-${c.key}`}
                aria-pressed={selected}
                aria-label={c.name}
              >
                {selected && (
                  <span className="bg-white/80 rounded-full w-7 h-7 flex items-center justify-center text-gray-900 font-black">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Shape picker */}
      <div className="mb-2">
        <p className="text-base font-bold text-gray-900 mb-3">Pick a symbol</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {JR_BRAND_SHAPES.map((s) => {
            const selected = shapeKey === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => pickShape(s.key)}
                className={`rounded-2xl py-4 ring-2 transition-all flex flex-col items-center gap-2 ${
                  selected
                    ? "bg-yellow-50 ring-yellow-500 shadow-lg -translate-y-0.5"
                    : "bg-white ring-slate-200 hover:ring-yellow-400 hover:shadow-md"
                }`}
                data-testid={`mission-5-shape-${s.key}`}
                aria-pressed={selected}
              >
                <span className="text-4xl" aria-hidden="true">{s.emoji}</span>
                <span className="text-sm font-bold text-gray-800">{s.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={5}
          missionTitle="Brand Your Business"
          message={`Your brand "${name.trim()}" looks amazing! Every real business needs a brand like this.`}
          continueLabel="Back to CEO City"
          onContinue={() => navigate("/elementary/demo")}
        />
      )}
    </ElementaryMissionPage>
  );
}
